import type { User } from 'c-entity-types';
import type { Config } from 'src/config';

import { mergeSystemSearchConfig as mergeFunction } from 'c-system-search';
import { omit } from 'ramda';
import * as pages from './Pages';
import * as slices from './Slices';

export const config: Config = {
    mergeFunction,
    reducers: {
        campaignReporting: slices.campaignReportingReducer,
        skin: slices.skinReducer,
        role: slices.roleReducer,
        permission: slices.permissionReducer,
        notificationReport: slices.notificationReportReducer,
        user: slices.userReducer,
        buyer: slices.buyerReducer,
        owner: slices.ownerReducer,
        campaign: slices.campaignReducer,
        agency: slices.agencyReducer,
        client: slices.clientReducer,
        buyerCost: slices.buyerCostReducer,
        data_Status_Line_Items: slices.data_Status_Line_ItemsReducer,
    },
    EntitySchemaActions: {
        CampaignReporting: {
            actions: slices.campaignReportingActions as any,
            entityName: 'CampaignReporting' as any,
            selectors: slices.campaignReportingSelectors as any,
            thunks: slices.campaignReportingThunks as any,
        },
        Skin: {
            actions: slices.skinActions as any,
            entityName: 'Skin' as any,
            selectors: slices.skinSelectors as any,
            thunks: slices.skinThunks as any,
        },
        Role: {
            actions: slices.roleActions as any,
            entityName: 'Role',
            selectors: slices.roleSelectors as any,
            thunks: slices.roleThunks as any,
        },
        Permission: {
            actions: slices.permissionActions as any,
            entityName: 'Permission',
            selectors: slices.permissionSelectors,
            thunks: slices.permissionThunks,
        },
        NotificationReport: {
            actions: slices.notificationReportActions as any,
            entityName: 'NotificationReport',
            selectors: slices.notificationReportSelectors,
            thunks: slices.notificationReportThunks,
        },
        User: {
            actions: slices.userActions as any,
            entityName: 'User',
            selectors: slices.userSelectors as any,
            thunks: slices.userThunks as any,
        },
        Buyer: {
            actions: slices.buyerActions as any,
            entityName: 'Buyer',
            selectors: slices.buyerSelectors as any,
            thunks: slices.buyerThunks as any,
        },
        Owner: {
            actions: slices.ownerActions as any,
            entityName: 'Owner',
            selectors: slices.ownerSelectors as any,
            thunks: slices.ownerThunks as any,
        },
        Campaign: {
            actions: slices.campaignActions as any,
            entityName: 'Campaign',
            selectors: slices.campaignSelectors as any,
            thunks: slices.campaignThunks as any,
        },
        Agency: {
            actions: slices.agencyActions as any,
            entityName: 'Agency',
            selectors: slices.agencySelectors,
            thunks: slices.agencyThunks,
        },
        Client: {
            actions: slices.clientActions as any,
            entityName: 'Client',
            selectors: slices.clientSelectors as any,
            thunks: slices.clientThunks as any,
        },
        Displays_Owner: {
            actions: slices.displaysOwnerActions as any,
            entityName: 'Displays_Owner',
            selectors: slices.displaysOwnerSelectors as any,
            thunks: slices.displaysOwnerThunks as any,
        },
        BuyerCost: {
            actions: slices.buyerCostActions as any,
            entityName: 'BuyerCost',
            selectors: slices.buyerCostSelectors,
            thunks: slices.buyerCostThunks,
        },
        DataStatusLineItems: {
            actions: slices.data_Status_Line_ItemsActions as any,
            entityName: 'DataStatusLineItems',
            selectors: slices.data_Status_Line_ItemsSelectors,
            thunks: slices.data_Status_Line_ItemsThunks,
        },
    },
    navigation: {
        groups: {
            2: {
                items: {
                    1: {
                        id: 'admin-home',
                        label: 'Navigation.admin',
                        icon: pages.AdminHomePage.systemSearch.icon,
                        link: pages.AdminHomePage.systemSearch.route,
                        permissions: pages.AdminHomePage.systemSearch.permissions,
                        hideIfNoChildren: true,
                        items: {
                            1: {
                                id: 'data-status',
                                icon: pages.DataStatusPage.systemSearch.icon,
                                label: 'Navigation.dataStatus',
                                link: pages.DataStatusPage.systemSearch.route,
                                permissions: pages.DataStatusPage.systemSearch?.permissions ?? [],
                            },
                            2: {
                                id: 'data_status-line-items',
                                icon: pages.DataStatusLineItemsPage.systemSearch.icon,
                                label: 'Navigation.dataStatusLineItems',
                                link: pages.DataStatusLineItemsPage.systemSearch.route,
                                permissions:
                                    pages.DataStatusLineItemsPage.systemSearch?.permissions ?? [],
                            },
                            3: {
                                id: 'notifications-list',
                                icon: pages.NotificationsListPage.systemSearch.icon,
                                label: 'Navigation.adminNotifications',
                                link: pages.NotificationsListPage.systemSearch.route,
                                permissions:
                                    pages.NotificationsListPage.systemSearch?.permissions ?? [],
                            },
                            4: {
                                id: 'clients-list',
                                icon: pages.ClientListPage.systemSearch.icon,
                                label: 'Navigation.clients',
                                link: pages.ClientListPage.systemSearch.route,
                                permissions: pages.ClientListPage.systemSearch?.permissions ?? [],
                            },
                            5: {
                                id: 'agencies-list',
                                icon: pages.AgencyListPage.systemSearch.icon,
                                label: 'Navigation.agencies',
                                link: pages.AgencyListPage.systemSearch.route,
                                permissions: pages.AgencyListPage.systemSearch?.permissions ?? [],
                            },
                            6: {
                                id: 'buyers-list',
                                icon: pages.BuyerListPage.systemSearch.icon,
                                label: 'Navigation.buyers',
                                link: pages.BuyerListPage.systemSearch.route,
                                permissions: pages.BuyerListPage.systemSearch?.permissions ?? [],
                            },
                            7: {
                                id: 'skin-list',
                                label: 'Navigation.adminSkins',
                                icon: pages.SkinListPage.systemSearch.icon,
                                link: pages.SkinListPage.systemSearch.route,
                                permissions: pages.SkinListPage.systemSearch.permissions,
                            },
                            8: {
                                id: 'market-list',
                                label: 'Navigation.adminMarkets',
                                icon: pages.MarketListPage.systemSearch.icon,
                                link: pages.MarketListPage.systemSearch.route,
                                permissions: pages.MarketListPage.systemSearch.permissions,
                            },
                            9: {
                                id: 'user-list',
                                label: 'Navigation.adminUsers',
                                icon: pages.UserListPage.systemSearch.icon,
                                link: pages.UserListPage.systemSearch.route,
                                permissions: pages.UserListPage.systemSearch.permissions,
                            },
                            10: {
                                id: 'role-list',
                                label: 'Navigation.adminRoles',
                                icon: pages.RoleListPage.systemSearch.icon,
                                link: pages.RoleListPage.systemSearch.route,
                                permissions: pages.RoleListPage.systemSearch.permissions,
                            },
                            11: {
                                id: 'impersonate-user',
                                label: 'Navigation.adminImpersonateUser',
                                icon: pages.ImpersonateUserPage.systemSearch.icon,
                                link: pages.ImpersonateUserPage.systemSearch.route,
                                permissions: pages.ImpersonateUserPage.systemSearch.permissions,
                            },
                            11.5: {
                                id: 'usage-overview',
                                label: 'Navigation.adminUsage',
                                icon: pages.UsageOverviewPage.systemSearch.icon,
                                link: pages.UsageOverviewPage.systemSearch.route,
                                permissions: pages.UsageOverviewPage.systemSearch.permissions,
                            },
                            12: {
                                id: 'frame-search',
                                label: 'Navigation.adminFrameSearch',
                                icon: pages.FrameSearchPage.systemSearch.icon,
                                link: pages.FrameSearchPage.systemSearch.route,
                                permissions: pages.FrameSearchPage.systemSearch.permissions,
                            },
                            13: {
                                id: 'task-queue-list',
                                icon: pages.TaskQueuePage.systemSearch.icon,
                                label: 'Navigation.popDisplaysTaskQueue',
                                link: pages.TaskQueuePage.systemSearch.route,
                                permissions: pages.TaskQueuePage.systemSearch?.permissions ?? [],
                            },
                            14: {
                                id: 'dashboard-setup',
                                label: 'Navigation.adminDashboardSetup',
                                icon: pages.DashboardSetupPage.systemSearch.icon,
                                link: pages.DashboardSetupPage.systemSearch.route,
                                permissions: pages.DashboardSetupPage.systemSearch.permissions,
                            },
                            15: {
                                id: 'summary-setup',
                                label: 'Navigation.adminSummarySetup',
                                icon: pages.SummarySetupPage.systemSearch.icon,
                                link: pages.SummarySetupPage.systemSearch.route,
                                permissions: pages.SummarySetupPage.systemSearch.permissions,
                            },
                        },
                    },
                },
            },
        },
    },
    pages: [
        pages.AuthorizeMcpPage,
        pages.SkinListPage,
        pages.DashboardSetupPage,
        pages.EditDashboardConfigPage,
        pages.SummarySetupPage,
        pages.EditSummaryConfigPage,
        pages.RoleListPage,
        pages.UserListPage,
        pages.AdminHomePage,
        pages.EditSkinPage,
        pages.EditRolePage,
        pages.NewUserPage,
        pages.EditUserPage,
        pages.ImpersonateUserPage,
        pages.MarketListPage,
        pages.FrameSearchPage,
        pages.TaskQueuePage,
        pages.DataStatusPage,
        pages.AgencyListPage,
        pages.AgencyOverviewPage,
        pages.ClientListPage,
        pages.BuyerListPage,
        pages.DataStatusLineItemsPage,
        pages.NotificationsListPage,
        pages.NotificationDetailPage,
        pages.UsageOverviewPage,
        pages.UsageFeaturesPage,
        pages.UsageOrganisationPage,
        pages.UsageUserPage,
    ],
    systemSearch: {},
    widgets: [],
    entityMapper: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        User: (user: User) => {
            if (Array.isArray(user?.permissions)) {
                // eslint-disable-next-line no-param-reassign
                user.permissionNames = user.permissions.map(p => p.name);
                // eslint-disable-next-line no-param-reassign
                user = omit(['permissions'], user);
            }

            return user as any;
        },
    },
};
